import { useEffect, useRef, useState } from 'react';

import { useInterval } from 'react-use';

import { type UtcMilliseconds } from '@pkg/types';
import { msToTime, timeUtcMsDiff } from '@pkg/utils';

import { timeAgoLabel } from './useTimeAgo.utilities';

export interface TimeAgoOptions {
    preferDaysOverWeeks?: boolean;
    interval?: number;
}

// We cannot use `dayjs` for this simply because leveraging the
// `updateLocale()` plugin will overwrite locales globally.
export function useTimeAgo(
    targetUtcMs: UtcMilliseconds = 0,
    { preferDaysOverWeeks = false, interval = 0 }: TimeAgoOptions,
) {
    const autoUpdate = useRef(Boolean(interval));
    const [difference, setDifference] = useState(timeUtcMsDiff(targetUtcMs));

    useEffect(() => {
        autoUpdate.current = Boolean(interval);
    }, [interval]);

    useEffect(() => {
        setDifference(timeUtcMsDiff(targetUtcMs));
    }, [targetUtcMs, preferDaysOverWeeks]);

    useInterval(
        () => {
            setDifference(timeUtcMsDiff(targetUtcMs));
        },
        autoUpdate.current ? interval : null,
    );

    const timeData = msToTime(difference);

    return {
        difference,
        timeData,
        // It might be preferred to flatten this object.
        label: {
            full: timeAgoLabel(timeData, { preferDaysOverWeeks }),
            shorthand: timeAgoLabel(timeData, { preferDaysOverWeeks, variant: 'shorthand' }),
            compact: timeAgoLabel(timeData, { preferDaysOverWeeks, variant: 'compact' }),
        },
    };
}
